'use client';

import { useEffect, useRef } from 'react';
import { isValidPhoneNumber } from 'libphonenumber-js';

import { fromApiPhone } from '@nutralab/api';

import { useAuth } from './AuthProvider';

import { isDialCodeOnly } from '@/shared/utils';

type ContactField = 'name' | 'phone' | 'email';

const CONTACT_FIELDS: ContactField[] = ['name', 'phone', 'email'];

interface ContactForm {
    getValue: (field: ContactField) => string | undefined;
    setValue: (field: ContactField, value: string) => void;
}

/**
 * Fills the empty contact fields of a form (name, phone, email) from the signed-in user's profile: when the form
 * opens for a user, and when a guest signs in while it's open. What the user typed is kept.
 */
export function useProfilePrefill(form: ContactForm): void {
    const { user } = useAuth();
    // Runs on a new profile only: a field the user cleared on purpose isn't filled again on the next render.
    const formRef = useRef(form);
    formRef.current = form;

    useEffect(() => {
        if (!user) {
            return;
        }

        const phone = fromApiPhone(user.phone);
        const values: Record<ContactField, string> = {
            name: user.name,
            phone: isValidPhoneNumber(phone) ? phone : '',
            email: user.email ?? '',
        };

        CONTACT_FIELDS.forEach((field) => {
            const current = formRef.current.getValue(field) ?? '';
            // An untouched phone field already holds the dial code (`+52`).
            const isEmpty = !current || (field === 'phone' && isDialCodeOnly(current));
            if (values[field] && isEmpty) {
                formRef.current.setValue(field, values[field]);
            }
        });
    }, [user]);
}
